import { evalCondition, isRequired, isVisible, isFilled, type FieldSpec, type FormDoc, type FormChoice } from "@gsignatures/core";
import { useEffect, useState } from "react";
import { ActionBar } from "../components/ActionBar";
import { Icon } from "../components/Icon";
import { Sheet } from "../components/Modal";
import { Field, fieldDomId, isShort } from "../form/Field";
import { mailto, prefersReducedMotion } from "../lib/format";
import { useFocusHeading } from "../lib/hooks";
import type { Answers } from "../state/answers";
import type { FlowCtx } from "../state/flow";
import { S } from "../strings";
import { DocViewer } from "../viewer/DocViewer";
import { formOverlay } from "../viewer/FormOverlay";

interface Props {
  ctx: FlowCtx;
  doc: FormDoc;
  answers: Answers | null;
  /** From Review's "Edit": the question to open on. */
  editField: string | null;
  onEdited: () => void;
}

export function TaxScreen({ ctx, doc, answers, editField, onEdited }: Props) {
  const [choosing, setChoosing] = useState(false);
  if (!doc.pack || !answers || choosing) {
    return <Chooser ctx={ctx} doc={doc} onChosen={() => setChoosing(false)} />;
  }
  return <FormFill ctx={ctx} doc={doc} a={answers} editField={editField} onEdited={onEdited} onChangeAnswer={() => setChoosing(true)} />;
}

/* ---------------- "Are you a U.S. person?" ---------------- */

function Chooser({ ctx, doc, onChosen }: { ctx: FlowCtx; doc: FormDoc; onChosen: () => void }) {
  const { w, api } = ctx;
  const heading = useFocusHeading();
  const [picked, setPicked] = useState<FormChoice["value"] | null>(doc.pack ? "no" : null);
  const [busy, setBusy] = useState(false);
  const pick = async (c: FormChoice) => {
    setPicked(c.value);
    if (!c.pack) return;
    if (doc.pack === c.pack) {
      onChosen();
      return;
    }
    setBusy(true);
    const s = await ctx.run(() => api.chooseForm(doc.id, c.value === "yes"));
    setBusy(false);
    if (s) {
      ctx.update(s);
      onChosen();
    }
  };
  const chosen = doc.chooser.choices.find((c) => c.value === picked);
  return (
    <main className="page" id="main">
      <div className="panel panel-pad chooser-panel">
        <div className="stack">
          <div>
            <div className="eyebrow">{S.chooser.eyebrow}</div>
            <h1 className="title" ref={heading} tabIndex={-1}>
              {S.chooser.title}
            </h1>
            <p className="lede">{S.chooser.lede(w)}</p>
          </div>
          <fieldset className="choice-set">
            <legend className="label big">{doc.chooser.question}</legend>
            <div className="choice-row">
              {doc.chooser.choices.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  className={`choice ${picked === c.value ? "on" : ""}`}
                  aria-pressed={picked === c.value}
                  disabled={busy}
                  onClick={() => void pick(c)}
                >
                  <b>{c.label}</b>
                  <span>{c.detail}</span>
                </button>
              ))}
            </div>
          </fieldset>
          <details className="more">
            <summary className="btn-link">
              {S.chooser.whatCounts} <Icon n="down" size={14} />
            </summary>
            <p className="help">{doc.chooser.help}</p>
          </details>
          <div aria-live="polite">
            {chosen && !chosen.pack && (
              <div className="note flag">
                <Icon n="info" />
                <span>
                  {S.chooser.unavailable(w)}{" "}
                  <a className="inline-link" href={mailto(w.contactEmail)}>
                    {w.contactEmail}
                  </a>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

/* ---------------- the form, section by section ---------------- */

function FormFill({ ctx, doc, a, editField, onEdited, onChangeAnswer }: { ctx: FlowCtx; doc: FormDoc; a: Answers; editField: string | null; onEdited: () => void; onChangeAnswer: () => void }) {
  const { w, api } = ctx;
  const { pack, values, validation } = a;
  const heading = useFocusHeading(editField ? "edit" : "fill");
  const [sheet, setSheet] = useState(false);
  const [busy, setBusy] = useState(false);
  const byId = new Map(pack.fields.map((f) => [f.id, f]));
  const { done, total } = validation.progress;
  const pct = total ? Math.round((done / total) * 100) : 100;
  const stops = (pack.stopWhen ?? []).filter((s) => evalCondition(s.when, values));
  // The first required question still empty or wrong, in the order the sections show them.
  const nextOpen = pack.sections
    .flatMap((sec) => sec.fields)
    .map((id) => byId.get(id))
    .find(
      (f): f is FieldSpec =>
        !!f && f.type !== "toggle" && isRequired(f, values, a.ctx) && (!isFilled(values[f.id]) || (validation.byField[f.id] ?? []).some((i) => i.severity === "error")),
    );
  const goNext = () => {
    if (!nextOpen) return;
    const el = document.getElementById(fieldDomId(nextOpen.id));
    el?.focus({ preventScroll: true });
    el?.scrollIntoView({ block: "center", behavior: prefersReducedMotion() ? "auto" : "smooth" });
  };

  // Arriving from Review's "Edit": open on that question.
  useEffect(() => {
    if (!editField) return;
    const el = document.getElementById(fieldDomId(editField));
    if (el) {
      el.focus({ preventScroll: true });
      el.scrollIntoView({ block: "center" });
    }
    onEdited();
  }, [editField, onEdited]);

  async function toReview() {
    if (busy) return;
    const first = a.touchAll();
    if (first) {
      const el = document.getElementById(fieldDomId(first));
      el?.focus({ preventScroll: true });
      el?.scrollIntoView({ block: "center" });
      ctx.toast(S.tax.needsFix);
      return;
    }
    setBusy(true);
    const ok = await a.flush();
    if (!ok) {
      setBusy(false);
      return;
    }
    const s = await ctx.run(() => api.session());
    setBusy(false);
    if (!s) return;
    ctx.update(s);
    if (s.step.step === "form" || s.step.step === "chooseForm") {
      ctx.toast(S.tax.needsFix);
      return;
    }
    // Pictures to add (an ID proof) come next, if the sender asked for any.
    ctx.go(s.documents.some((d) => d.kind === "upload") ? "upload" : "review");
  }

  // With pictures still to add (an ID proof), this leads there rather than to the review.
  const picturesNext = ctx.session.documents.some((d) => d.kind === "upload");

  const sectionView = pack.sections.map((sec) => {
    const specs = sec.fields.map((id) => byId.get(id)).filter((f): f is FieldSpec => !!f && isVisible(f, values));
    if (specs.length === 0) return null;
    const req = specs.filter((f) => f.type !== "toggle" && isRequired(f, values, a.ctx));
    const ok = req.filter((f) => isFilled(values[f.id]) && !(validation.byField[f.id] ?? []).some((i) => i.severity === "error")).length;
    // Two short questions in a row share a line.
    const rows: FieldSpec[][] = [];
    for (let i = 0; i < specs.length; i++) {
      const f = specs[i]!;
      const g = specs[i + 1];
      if (isShort(f) && g && isShort(g)) {
        rows.push([f, g]);
        i++;
      } else rows.push([f]);
    }
    const hid = `sec-${sec.id}`;
    return (
      <section className="section" key={sec.id} aria-labelledby={hid}>
        <div className="section-head">
          <h2 id={hid}>{sec.title}</h2>
          {sec.optional && req.length === 0 ? (
            <span className="count">{S.common.optional}</span>
          ) : req.length > 0 && ok === req.length ? (
            <span className="count ok">
              <Icon n="check" /> {S.tax.sectionDone}
            </span>
          ) : req.length > 0 ? (
            <span className="count">{S.tax.sectionCount(ok, req.length)}</span>
          ) : null}
        </div>
        <div className="section-body">
          {sec.intro && <p className="help">{sec.intro}</p>}
          {rows.map((r) =>
            r.length === 2 ? (
              <div className="grid2" key={r[0]!.id}>
                <Field spec={r[0]!} a={a} issuer={pack.issuer} />
                <Field spec={r[1]!} a={a} issuer={pack.issuer} />
              </div>
            ) : (
              <Field key={r[0]!.id} spec={r[0]!} a={a} issuer={pack.issuer} />
            ),
          )}
        </div>
      </section>
    );
  });

  const preview = (bare: boolean) => (
    <DocViewer
      title={`${pack.title} (${S.tax.revision(pack.revision)})`}
      url={api.fileUrl(doc.id)}
      withCredentials={!api.fake}
      pageCount={1}
      pill={bare ? undefined : S.tax.live}
      bare={bare}
      {...(api.fake ? { demoPath: api.fileUrl(doc.id) } : {})}
      overlay={formOverlay({ pack, values, activeField: a.focus, mode: "fill" })}
      focusKey={a.focus ?? null}
    />
  );

  return (
    <>
      <main className="page" id="main">
        <div className="split form-split">
          <div className="stack">
            <div className="panel panel-pad stack tight-14">
              <div>
                <div className="eyebrow">
                  {pack.title} · {pack.issuer}
                </div>
                <h1 className="title" ref={heading} tabIndex={-1}>
                  {S.tax.title}
                </h1>
                <p className="lede">{S.tax.lede(w)}</p>
              </div>
              <div className="progress">
                <div
                  className="bar"
                  role="progressbar"
                  aria-label={S.tax.progressLabel}
                  aria-valuemin={0}
                  aria-valuemax={total}
                  aria-valuenow={done}
                  aria-valuetext={S.tax.progressCount(done, total)}
                >
                  <i style={{ width: `${pct}%` }} />
                </div>
                <span className="mono faint progress-count">{S.tax.progressCount(done, total)}</span>
              </div>
              <div className="row between">
                <button type="button" className="btn-link small" onClick={onChangeAnswer}>
                  <Icon n="back" size={14} /> {S.chooser.change}
                </button>
                {nextOpen && (
                  <button type="button" className="btn-link small" onClick={goNext}>
                    {S.tax.nextUnanswered} <Icon n="down" size={14} />
                  </button>
                )}
              </div>
              <button type="button" className="btn btn-quiet btn-sm preview-fab self-start" onClick={() => setSheet(true)}>
                <Icon n="eye" /> {S.tax.seeOnForm}
              </button>
            </div>
            {stops.length > 0 && (
              <div className="note bad" role="status">
                <Icon n="alert" />
                <span>
                  {stops[0]!.message}{" "}
                  <a className="inline-link" href={mailto(w.contactEmail)}>
                    {w.contactEmail}
                  </a>
                </span>
              </div>
            )}
            {sectionView}
            <div className="row between desk-actions">
              <button type="button" className="btn btn-quiet" onClick={() => ctx.go("offer")}>
                <Icon n="back" /> {S.common.back}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => void toReview()} disabled={busy} aria-busy={busy}>
                {picturesNext ? S.common.continue : S.tax.review} <Icon n="chev" />
              </button>
            </div>
          </div>
          <div className="sticky-col preview-col">{preview(false)}</div>
        </div>
      </main>
      <ActionBar>
        <button type="button" className="btn btn-quiet" onClick={() => setSheet(true)}>
          <Icon n="eye" /> {S.tax.formShort}
        </button>
        <button type="button" className="btn btn-primary" onClick={() => void toReview()} disabled={busy}>
          {picturesNext ? S.common.continue : S.tax.reviewShort}
        </button>
      </ActionBar>
      {sheet && (
        <Sheet title={S.tax.sheetTitle(pack.title)} onClose={() => setSheet(false)}>
          {preview(true)}
        </Sheet>
      )}
    </>
  );
}
